import { useMemo } from 'react';

import type { IFuseOptions } from 'fuse.js';

import type { ColumnFiltersState } from '@tanstack/react-table';

import type { CatalogItem } from '@/entities/catalog-item';
import { SEARCH_COLUMN_ID } from '../constants';
import { createCatalogFuse } from '../lib/catalogFuse';
import { getCatalogRowId } from '../lib/getCatalogRowId';
import { matchCatalogCodes } from '../lib/gxCodeMatch';

/**
 * Derives the `search` column's `ColumnFiltersState` entry from `filters.search`
 * — a set of matched row ids, matching the same rules `useFuzzySearchedCatalogData`
 * used to apply as a pre-table data reshape: a code query (`GX` + digits) matches
 * `Code` directly, everything else fuzzy-matches via Fuse. Returns an empty array
 * when there's no active search, so callers can spread it directly into the
 * table's `columnFilters`.
 */
export function useSearchColumnFilter(
  items: CatalogItem[],
  search: string | null,
  options?: IFuseOptions<CatalogItem>
): ColumnFiltersState {
  const fuse = useMemo(() => createCatalogFuse(items, options), [items, options]);

  return useMemo((): ColumnFiltersState => {
    const query = search?.trim();
    if (!query) return [];

    const codeMatches = matchCatalogCodes(items, query);
    const matched = codeMatches ?? fuse.search(query).map(result => result.item);

    return [{ id: SEARCH_COLUMN_ID, value: new Set(matched.map(getCatalogRowId)) }];
  }, [fuse, items, search]);
}
